import React, { useEffect, useState } from "react";
import {
  Badge,
  Button,
  Col,
  Container,
  Form,
  Row,
  Table,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import {
  deleteServiceBookingAdmin,
  listServiceBookingsAdmin,
  updateServiceBookingAdmin,
} from "../../actions/serviceActions";
import {
  ADMIN_SERVICE_BOOKING_DELETE_RESET,
  ADMIN_SERVICE_BOOKING_UPDATE_RESET,
} from "../../constants/serviceConstants";
import Loader from "../../components/Loader";
import Message from "../../components/Message";

const STATUS_OPTIONS = ["all", "new", "confirmed", "completed", "cancelled"];

function formatAppointment(value) {
  if (!value) return "-";

  try {
    return new Intl.DateTimeFormat("en-GB", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(value));
  } catch {
    return "-";
  }
}

function statusVariant(status = "") {
  switch (status) {
    case "confirmed":
      return "success";
    case "completed":
      return "dark";
    case "cancelled":
      return "secondary";
    default:
      return "warning";
  }
}

export default function AdminServiceBookingsScreen() {
  const dispatch = useDispatch();
  const [status, setStatus] = useState("all");

  const bookingList = useSelector((state) => state.adminServiceBookingList);
  const { loading, error, bookings = [] } = bookingList;

  const bookingUpdate = useSelector(
    (state) => state.adminServiceBookingUpdate,
  );
  const {
    loading: loadingUpdate,
    success: successUpdate,
    error: errorUpdate,
  } = bookingUpdate;

  const bookingDelete = useSelector(
    (state) => state.adminServiceBookingDelete,
  );
  const {
    loading: loadingDelete,
    success: successDelete,
    error: errorDelete,
  } = bookingDelete;

  useEffect(() => {
    if (successUpdate) {
      dispatch({ type: ADMIN_SERVICE_BOOKING_UPDATE_RESET });
    }

    if (successDelete) {
      dispatch({ type: ADMIN_SERVICE_BOOKING_DELETE_RESET });
    }

    dispatch(listServiceBookingsAdmin(status));
  }, [dispatch, status, successDelete, successUpdate]);

  const updateStatusHandler = (bookingId, nextStatus) => {
    dispatch(updateServiceBookingAdmin(bookingId, { status: nextStatus }));
  };

  const notesHandler = (booking) => {
    const nextNotes = window.prompt("Admin notes", booking.adminNotes || "");

    if (nextNotes !== null) {
      dispatch(
        updateServiceBookingAdmin(booking._id, {
          adminNotes: nextNotes,
        }),
      );
    }
  };

  const deleteHandler = (bookingId) => {
    if (window.confirm("Delete this reservation request?")) {
      dispatch(deleteServiceBookingAdmin(bookingId));
    }
  };

  return (
    <Container className="py-4">
      <Row className="align-items-center g-3 mb-3">
        <Col>
          <h1>Service Reservations</h1>
          <div className="text-muted">
            Review customer appointment requests for in-house services.
          </div>
        </Col>

        <Col md="auto" className="d-flex gap-2 flex-wrap align-items-center">
          <Form.Select
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            aria-label="Filter reservations by status"
            style={{ minWidth: 180 }}
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option === "all" ? "All statuses" : option}
              </option>
            ))}
          </Form.Select>

          <Link to="/admin/services" className="btn btn-outline-dark">
            Services
          </Link>
        </Col>
      </Row>

      {loadingUpdate && <Message>Updating reservation...</Message>}
      {loadingDelete && <Message>Deleting reservation...</Message>}
      {errorUpdate && <Message variant="danger">{errorUpdate}</Message>}
      {errorDelete && <Message variant="danger">{errorDelete}</Message>}
      {error && <Message variant="danger">{error}</Message>}
      {loading && <Loader />}

      <Table striped bordered hover responsive className="align-middle">
        <thead>
          <tr>
            <th>Appointment</th>
            <th>Service</th>
            <th>Customer</th>
            <th>Message</th>
            <th>Status</th>
            <th style={{ width: "220px" }}>Actions</th>
          </tr>
        </thead>

        <tbody>
          {bookings.map((booking) => (
            <tr key={booking._id}>
              <td>{formatAppointment(booking.appointmentAt)}</td>
              <td>
                <div className="fw-semibold">
                  {booking.serviceSnapshot?.name || booking.service?.name}
                </div>
                <div className="text-muted small">
                  {booking.serviceSnapshot?.priceLabel || "-"}
                </div>
              </td>
              <td>
                <div className="fw-semibold">{booking.customerName}</div>
                <div>
                  <a href={`mailto:${booking.customerEmail}`}>
                    {booking.customerEmail}
                  </a>
                </div>
                <div>
                  <a href={`tel:${booking.customerPhone}`}>
                    {booking.customerPhone}
                  </a>
                </div>
              </td>
              <td style={{ minWidth: 220, maxWidth: 360 }}>
                <div
                  title={booking.message}
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {booking.message || "-"}
                </div>
                {booking.adminNotes ? (
                  <div className="text-muted small mt-1">
                    Notes: {booking.adminNotes}
                  </div>
                ) : null}
              </td>
              <td>
                <Badge
                  bg={statusVariant(booking.status)}
                  text={booking.status === "new" ? "dark" : undefined}
                >
                  {booking.status}
                </Badge>
              </td>
              <td>
                <div className="d-flex gap-2 flex-wrap">
                  <Form.Select
                    size="sm"
                    value={booking.status}
                    disabled={loadingUpdate}
                    onChange={(event) =>
                      updateStatusHandler(booking._id, event.target.value)
                    }
                    aria-label={`Update ${booking.customerName} reservation status`}
                  >
                    {STATUS_OPTIONS.filter((option) => option !== "all").map(
                      (option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ),
                    )}
                  </Form.Select>

                  <Button
                    variant="outline-dark"
                    size="sm"
                    onClick={() => notesHandler(booking)}
                  >
                    Notes
                  </Button>

                  <Button
                    variant="outline-danger"
                    size="sm"
                    disabled={loadingDelete}
                    onClick={() => deleteHandler(booking._id)}
                  >
                    Delete
                  </Button>
                </div>
              </td>
            </tr>
          ))}

          {bookings.length === 0 && !loading ? (
            <tr>
              <td colSpan={6} className="text-center text-muted">
                No service reservations found.
              </td>
            </tr>
          ) : null}
        </tbody>
      </Table>
    </Container>
  );
}
